import React, { useState, useEffect, useContext } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { UserContext } from "../UserContext";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormGroup,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams, useLocation } from "react-router-dom";

import { getJobOrder, putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import { sendEmailQueue } from "../API/emailAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from "@mui/icons-material/Circle";

import TermiteLocationCard from "./FormComponents/TermiteLocationCard";
import ExterraCard from "./FormComponents/ExterraCard";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function EditTermiteControlForm() {
  const { id } = useParams();
  const location = useLocation();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const navigate = useNavigate();

  // Get returnTo parameter
  const returnTo = new URLSearchParams(location.search).get('returnTo');

  // RHF: form state for locations & exterra stations
  const methods = useForm({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      locations: [],
      exterra_bait_stations: [],
      termite_recommendations: [],
    },
  });
  const { control, register, setValue, getValues } = methods;

  // Field array for Location cards
  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
    replace: replaceLocations,
  } = useFieldArray({ control, name: "locations" });


  useEffect(() => {
  const fetchJobOrder = async () => {
    try {
      setIsLoading(true);

      const data = await getJobOrder(id);
      const clientData = await getClient(data.client_id);
      setClient(clientData || {});
      setJobOrder(data || {});
      
    } catch (err) {
      setErrorMessage("Error fetching job order: " + err.message); 
    } finally {
      setIsLoading(false);
    }
  };

  if (id) { // Only fetch if id exists
    fetchJobOrder();
  }
}, [id]);

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  const STRUCTURE_TYPE_OPTIONS = [
    { key: "residential", label: "Residential" },
    { key: "commercial",  label: "Commercial"  },
    { key: "industrial",  label: "Industrial"  },
  ];

  // Normalize truthy values (true / "true" / 1 / "yes", etc.)
  const isTrue = (v) =>
    typeof v === "boolean" ? v
    : typeof v === "number" ? v === 1
    : typeof v === "string" ? ["true","1","yes","y","on"].includes(v.trim().toLowerCase())
    : false;

  const sd = jobOrder?.structure_details ?? {};

  const normalize = (v) => String(v ?? "").trim().toLowerCase();

  const ta = normalize(jobOrder?.termite_activity);

  // helper: parse action_taken "Baiting System Installation/ AG Monitoring in ___"
  const decodeActionTaken = (s = "") => {
    const t = s.trim();
    if (!t) return { action_taken: "", bait_monitor_location: "" };
    if (t.startsWith("Baiting System Installation/ AG Monitoring in")) {
      return {
        action_taken: "baiting",
        bait_monitor_location: t.replace("Baiting System Installation/ AG Monitoring in", "").trim(),
      };
    }
    const map = {
      "Soil Treatment": "soil",
      "Spraying": "spraying",
      "Monitoring only": "monitoring",
    };
    return { action_taken: map[t] || "", bait_monitor_location: "" };
  };

  const primedRef = React.useRef(false);

  const LOCATION_OPTIONS_SET = new Set([
    "Living Room / Sala",
    "Bedroom 1",
    "Bedroom 2",
    "Bedroom 3",
    "Kitchen",
    "Dining Area",
    "Bathroom 1",
    "Bathroom 2",
    "Closet / Cabinets",
    "Hallways",
    "Staircase",
    "Ceiling",
    "Baseboard",
    "Floor",
    "Exterior Perimeter",
    "Garage",
    "Garden",
    "Storage Area",
    "Other",
  ]);


  // Prime RHF cards whenever the fetched job order changes
  useEffect(() => {
    if (!jobOrder?.id || primedRef.current) return;

    // Locations → RHF

    const mappedLocations = (jobOrder?.inspection_report || [])
      .filter(r => r && (r.location || r.findings || r.action_taken || r.remarks))
      .map(r => {
        const { action_taken, bait_monitor_location } =
          decodeActionTaken(r.action_taken || "");

        const rawLoc = (r.location || "").trim();
        const rawOther = (r.location_other || "").trim();

        const isLegacyCustom =
          rawLoc && !LOCATION_OPTIONS_SET.has(rawLoc);

        const uiLocation =
          rawOther ? "Other" : (isLegacyCustom ? "Other" : rawLoc);

        const uiLocationOther =
          rawOther ? rawOther : (isLegacyCustom ? rawLoc : "");

        return {
          location: uiLocation,
          location_other: uiLocationOther,
          findings: r.findings || "",
          action_taken,
          bait_monitor_location,
          remarks: r.remarks || "",
        };
      });


    // Exterra → RHF
    const mappedExterra = Array.isArray(jobOrder?.exterra_bait_stations)
      ? jobOrder.exterra_bait_stations
      : [];

    // Prime field-array and other values once
    replaceLocations(mappedLocations.length ? mappedLocations : []);
    setValue("exterra_bait_stations", mappedExterra, { shouldDirty: false });
    setValue("termite_recommendations", jobOrder?.termite_recommendations || [], { shouldDirty: false });

    primedRef.current = true; // prevent future overwrites while user is typing
  }, [jobOrder, replaceLocations, setValue]);

  // Encode back to the persisted "action_taken" string
  const encodeActionTaken = ({ action_taken, bait_monitor_location }) => {
    if (action_taken === "baiting") {
      return `Baiting System Installation/ AG Monitoring in ${bait_monitor_location || ""}`.trim();
    }
    const map = { soil: "Soil Treatment", spraying: "Spraying", monitoring: "Monitoring only" };
    return map[action_taken] || "";
  };

  // --- Payment defaults (keep order stable)
  const DEFAULT_PAYMENT_STATUS = [
    { label: "Paid", selection: false },
    { label: "COD", selection: false },
    { label: "For Collection", selection: false },
  ];

  const DEFAULT_PAYMENT_METHOD = [
    { label: "Cash",  selection: false },
    { label: "Check", selection: false },
    { label: "Free",  selection: false },
  ];

  // Local UI state (what the table renders)
  const [service_report_payment_status, setServiceReportPaymentStatus] = useState(DEFAULT_PAYMENT_STATUS);
  const [service_report_payment_method, setServiceReportPaymentMethod] = useState(DEFAULT_PAYMENT_METHOD);

  // Normalize incoming array/string → align with our defaults by label
  const normalizeByLabel = (incoming, defaults) => {
    if (Array.isArray(incoming)) {
      const map = new Map(incoming.map(i => [String(i.label || i.value).toLowerCase(), !!i.selection]));
      return defaults.map(d => ({ ...d, selection: !!map.get(d.label.toLowerCase()) }));
    }
    if (typeof incoming === "string") {
      const v = incoming.toLowerCase();
      return defaults.map(d => ({ ...d, selection: d.label.toLowerCase() === v }));
    }
    return defaults;
  };

  useEffect(() => {
    const status = normalizeByLabel(jobOrder?.service_report_payment_status, DEFAULT_PAYMENT_STATUS);
    const method = normalizeByLabel(jobOrder?.service_report_payment_method, DEFAULT_PAYMENT_METHOD);

    setServiceReportPaymentStatus(status);
    setServiceReportPaymentMethod(method);

    // keep outgoing payload in sync
    setJobOrder(prev => ({
      ...prev,
      service_report_payment_status: status,
      service_report_payment_method: method,
    }));
  }, [jobOrder?.service_report_payment_status, jobOrder?.service_report_payment_method]);

  const handlePaymentStatusChange = (statusIndex) => {
    const next = service_report_payment_status.map((s, i) => ({
      ...s,
      selection: i === statusIndex ? !s.selection : false,
    }));
    setServiceReportPaymentStatus(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_status: next }));
  };

  const handlePaymentMethodChange = (methodIndex) => {
    const next = service_report_payment_method.map((m, i) => ({
      ...m,
      selection: i === methodIndex ? !m.selection : false,
    }));
    setServiceReportPaymentMethod(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_method: next }));
  };


  // Handler for updating job order fields
  // const handleInputChange = (field, value) => {
  //   setJobOrder(prev => {
  //     const newState = {
  //       ...prev,
  //       [field]: value
  //     };

  //     // If changing the start date, ensure end date is not before start date
  //     if (field === 'job_date' && prev.end_date && value && value > prev.end_date) {
  //       newState.end_date = value; // Set end date to start date if it's earlier
  //     }

  //     // If changing the end date, ensure it's not before start date
  //     if (field === 'end_date' && prev.job_date && value && value < prev.job_date) {
  //       return prev; // Don't update if end date is before start date
  //     }

  //     return newState;
  //   });
  // };
  const handleInputChange = (field, value) => {
   setJobOrder(prev => ({ ...prev, [field]: value }));
  };

  // Handler for nested object updates
  const handleNestedChange = (parentField, field, value) => {
    setJobOrder(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };

  // Save function
  const handleSave = async () => {
    try {
      setIsSaving(true);

      // 1) Collect current form values (you already have these in handleSave)
      const form = getValues();

      // Rebuild inspection_report like in handleSave
      // const inspection_report = (form.locations || []).map(r => ({
      //   location: r.location || "",
      //   findings: r.findings || "",
      //   action_taken: encodeActionTaken(r),
      //   remarks: r.remarks || "",
      // }));
      const inspection_report = (form.locations || []).map((r) => {
        const resolvedLocation =
          r.location === "Other" ? (r.location_other || "").trim() : (r.location || "").trim();

        return {
          location: resolvedLocation,
          // keep this for backwards-compat/view rendering safety
          location_other: r.location === "Other" ? (r.location_other || "").trim() : "",
          findings: (r.findings || "").trim(),
          action_taken: encodeActionTaken(r),
          remarks: (r.remarks || "").trim(),
        };
      });


      const recommendations = form.termite_recommendations?.length
        ? form.termite_recommendations
        : (jobOrder.termite_recommendations || []);

      const updated = {
        ...jobOrder,
        inspection_report,
        exterra_bait_stations: form.exterra_bait_stations || [],
        termite_recommendations: recommendations,
        service_report_payment_status,
        service_report_payment_method,
      };

      // 2) Persist updates first
      await putJobOrder(id, updated);

      // 3) Call your field-mapper Lambda (same pattern as in TermiteControlForm)
      const payload = convertBooleans({ jobOrder: { ...jobOrder, ...updated }, updatedJobOrderParameters: updated, form });
      await submitDocumentFieldMapper(payload);

      // 4) Email the client with the generated links
      await handleSendEmail();

      setSuccessMessage("Service report updated and documents regenerated successfully!");
    } catch (err) {
      console.error(err);
      setErrorMessage(`Failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };


  // Recursively convert JS booleans to "True"/"False" strings for the PDF generator
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) return obj.map(convertBooleans);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, convertBooleans(v)]));
    }
    if (typeof obj === "boolean") return obj ? "True" : "False";
    return obj;
  };

  const formatDate = (dateString) => {
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return "Invalid Date";
      return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" }).format(d);
    } catch {
      return "Invalid Date";
    }
  };

  const handleSendEmail = async () => {
    // Build links to your generated files (adjust if you add more docs)
    const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
    const attachmentLinks = {
      // certificate_url: `${baseUrl}/${jobOrder.id}_certificate.pdf`,
      // reports_url:     `${baseUrl}/${jobOrder.id}_report.pdf`,
      receipts_url:     `${baseUrl}/${jobOrder.id}_final_receipt.pdf`,
    };

    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([k, url]) => {
        const label = k.replace(/_url$/, "").replace(/_/g, " ").toUpperCase();
        return `<li><a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body{font-family:Arial, sans-serif;margin:0;padding:0}
          .email-container{max-width:600px;margin:0 auto;padding:20px;border:1px solid #ddd}
          .header,.footer{text-align:center;padding:20px 0}
          .content{margin:20px 0}
          .content img{max-width:100%;height:auto;display:block;margin:0 auto}
        </style>
      </head>
      <body>
        <div class="email-container">
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" />
          </div>
          <div class="content">
            <p>Sir/Ma'am,</p>
            <p>Good day!</p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on ${formatDate(jobOrder?.job_date)}.</p>
            <p>We thank you and please let us know if you have any questions.</p>
            <p>Sincerely,<br/>Boaz Pest Control - Accounts Team</p>
            <ul>${attachmentLinksHTML}</ul>
            <hr />
          </div>
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration:none">
            <div class="footer">
              <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" />
              <hr/>
              <footer>
                <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
              </footer>
            </div>
          </a>
        </div>
      </body>
    </html>`;

    const payload = {
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email].filter(Boolean),
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    await sendEmailQueue(payload);
  };




  return (
    <FormProvider {...methods}>
      <BaseFormWrapper
        titleIcon={<ReceiptLongIcon />}
        title="Operations"
        subTitle="Service Reports"
        subTitleRoute="/manage-service-reports"
        formTitle="Edit Service Report"
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        isLoading={isLoading}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={2}>
                    <FormControl fullWidth>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrder?.status || ""}
                        label="Job Order Status"
                        onChange={(e) => handleInputChange('status', e.target.value)}
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client_id || ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder.client.address.house_unit_street || ""
                              }, ${jobOrder.client.address.barangay || ""
                              }, ${jobOrder.client.address.city || ""
                              }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder.client.address.zipcode || ""}`
                              : ""
                          }
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder.client?.contact
                          ? `${jobOrder.client.contact.lastname || ""}, ${jobOrder.client.contact.firstname || ""
                          }`
                          : ""
                      }
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder.client?.contact?.email?.email || ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder.client?.contact?.number
                          ? `${jobOrder.client.contact.number.type}: ${jobOrder.client.contact.number.number}`
                          : ""
                      }
                      disabled
                    />
                  </Grid>                
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {displayedJobTypes.map((option, idx) => (
                    <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {displayedJobDescriptions.map((option, idx) => (
                    <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{                    
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Appointment
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={6}>
                    <FormControl fullWidth>
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        name="appointment_type"
                        value={jobOrder?.appointment_type || ""}
                        label="appointment_type"
                        disabled
                      >
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={12} lg={6}>
                    <FormControl fullWidth>
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        name="treatment_type"
                        value={jobOrder?.treatment_type || ""}
                        label="treatment_type"
                        disabled
                      >
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Structure Type Section (editable) */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Structure Type</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  {STRUCTURE_TYPE_OPTIONS.map((option) => {
                    const isChecked = isTrue(jobOrder?.structure_type?.[option.key]) || false;
                    console.log(`Structure Type ${option.key}:`, jobOrder?.structure_type?.[option.key], 'isChecked:', isChecked);
                    
                    return (
                      <FormControlLabel
                        key={option.key}
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              console.log(`Changing ${option.key} to:`, e.target.checked);
                              const currentStructureType = jobOrder?.structure_type || {};
                              const updatedStructureType = {
                                ...currentStructureType,
                                [option.key]: e.target.checked
                              };
                              console.log('Updated structure type:', updatedStructureType);
                              handleInputChange('structure_type', updatedStructureType);
                            }}
                          />
                        }
                        label={option.label}
                      />
                    );
                  })}
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Structure Details (editable) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Structure Details
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Bedrooms"
                      type="number"
                      value={sd?.bedrooms || ""}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        const currentDetails = jobOrder?.structure_details || {};
                        const updatedDetails = {
                          ...currentDetails,
                          bedrooms: e.target.value
                        };
                        handleInputChange('structure_details', updatedDetails);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Restrooms"
                      type="number"
                      value={sd?.restrooms || ""}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        const currentDetails = jobOrder?.structure_details || {};
                        const updatedDetails = {
                          ...currentDetails,
                          restrooms: e.target.value
                        };
                        handleInputChange('structure_details', updatedDetails);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Floors"
                      type="number"
                      value={sd?.floors || ""}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        const currentDetails = jobOrder?.structure_details || {};
                        const updatedDetails = {
                          ...currentDetails,
                          floors: e.target.value
                        };
                        handleInputChange('structure_details', updatedDetails);
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Termite Activity (view-only) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Termite Activity
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{
                      width: "100%",
                      opacity: ta === "with" ? 1 : 0.75,
                    }}
                    control={
                      <Checkbox
                        checked={ta === "with"}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleInputChange('termite_activity', 'with');
                          } else if (ta === "with") {
                            handleInputChange('termite_activity', '');
                          }
                        }}
                      />
                    }
                    label="With Active Termites"
                  />

                  <FormControlLabel
                    sx={{
                      width: "100%",
                      opacity: ta === "without" ? 1 : 0.75,
                    }}
                    control={
                      <Checkbox
                        checked={ta === "without"}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleInputChange('termite_activity', 'without');
                          } else if (ta === "without") {
                            handleInputChange('termite_activity', '');
                          }
                        }}
                      />
                    }
                    label="Without Active Termites"
                  />
                </FormGroup>

                {ta !== "with" && ta !== "without" && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No selection
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Inspection & Pest Count (Mobile) — editable, wrapped in a parent card */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Inspection Report
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Stack spacing={2}>
                  {locationFields.map((loc, idx) => (
                    <TermiteLocationCard
                      key={loc.id}
                      control={control}
                      register={register}
                      index={idx}
                      removeLocation={removeLocation}
                    />
                  ))}
                  <Button
                    variant="contained"
                    disabled={locationFields.length >= 19}
                    fullWidth
                    onClick={() =>
                      appendLocation({
                        location: "",
                        location_other: "",
                        findings: "",
                        action_taken: "",
                        bait_monitor_location: "",
                        remarks: "",
                      })
                    }
                  >
                    Add New Location ({locationFields.length}/19)
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations Section - Editable */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Recommendations
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                {(() => {
                  const RECOMMENDATIONS = [
                    "Schedule termite control",
                    "Monitor regularly",
                    "Seal entry points with caulking or foam",
                    "Improve airflow, reduce moisture",
                    "Repair leaks, dry affected zones",
                    "Stagnant water / leaking pipes",
                    "Relocate or elevate storage",
                    "Closed room or area Inaccessible",
                    "Ensure room access for full inspection",
                  ];

                  const currentRecommendations = getValues("termite_recommendations") ?? [];

                  return (
                    <Stack spacing={1}>
                      {RECOMMENDATIONS.map((label) => {
                        const isChecked = currentRecommendations.includes(label);
                        return (
                          <FormControlLabel
                            key={label}
                            sx={{ width: "100%" }}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => {
                                  const updatedRecommendations = e.target.checked
                                    ? [...currentRecommendations, label]
                                    : currentRecommendations.filter((v) => v !== label);
                                  
                                  setJobOrder(prev => ({
                                    ...prev,
                                    termite_recommendations: updatedRecommendations
                                  }));
                                  setValue("termite_recommendations", updatedRecommendations, { shouldDirty: true });
                                }}
                              />
                            }
                            label={label}
                          />
                        );
                      })}
                    </Stack>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Exterra Section - Editable */}
          <Grid item xs={12}>
            <ExterraCard control={control} register={register} />
          </Grid>

          {/* Chemical Used Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Chemical Used / Batch # / Expiration
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: "bold" }}
                          >
                            Dilution
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Equipment Used
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(jobOrder.chemical_used || []).map((chem, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{chem.label}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chem.chemical || ""}
                                onChange={(e) => {
                                  const updatedChemicals = [...(jobOrder.chemical_used || [])];
                                  updatedChemicals[idx] = { ...updatedChemicals[idx], chemical: e.target.value };
                                  handleInputChange('chemical_used', updatedChemicals);
                                }}
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chem.dilution || ""}
                                onChange={(e) => {
                                  const updatedChemicals = [...(jobOrder.chemical_used || [])];
                                  updatedChemicals[idx] = { ...updatedChemicals[idx], dilution: e.target.value };
                                  handleInputChange('chemical_used', updatedChemicals);
                                }}
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                            {jobOrder.equipment_used?.length > idx ? (
                              <>
                                <TableCell>
                                  {jobOrder.equipment_used[idx].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={jobOrder.equipment_used[idx].usage}
                                    onChange={(e) => {
                                      const updatedEquipment = [...(jobOrder.equipment_used || [])];
                                      updatedEquipment[idx] = { ...updatedEquipment[idx], usage: e.target.checked };
                                      handleInputChange('equipment_used', updatedEquipment);
                                    }}
                                  />
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell />
                                <TableCell />
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Reminder Notes Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    Reminder Notes
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Reminder Notes"
                  value={jobOrder.reminder_notes || ""}
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                  onChange={(e) => handleInputChange('reminder_notes', e.target.value)}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Service Fee Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    Service Fee
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Service Fee"
                  value={jobOrder.service_fee || ""}
                  fullWidth
                  size="small"
                  onChange={(e) => handleInputChange('service_fee', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₱</InputAdornment>
                    )
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Status Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={<Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Payment Status</Typography>}
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableBody>
                      {(service_report_payment_status || []).map((statusOpt, idx) => {
                        const methodOpt = (service_report_payment_method || [])[idx] || {};
                        return (
                          <TableRow key={statusOpt.label} hover>
                            <TableCell>{statusOpt.label}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={statusOpt.selection}
                                onChange={() => handlePaymentStatusChange(idx)}
                                size="small"
                              />
                            </TableCell>

                            <TableCell align="center" sx={{ color: 'text.secondary', p: 0 }}>
                              •
                            </TableCell>
                            <TableCell />

                            <TableCell>{methodOpt.label || ""}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={methodOpt.selection}
                                onChange={() => handlePaymentMethodChange(idx)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Service Accomplished by Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Service Accomplished by:
                </Typography>
                <Grid container spacing={2}>
                  { (jobOrder.accomplished_by_technician || jobOrder.technicians || [])
                      .map((name, i) =>
                        typeof name === "string"
                          ? name
                          : `${name.firstname} ${name.lastname}`
                      )
                      .map((techName, i) => (
                    <Grid key={i} item xs={12}>
                      <TextField
                        label={`Technician ${i + 1}`}
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={techName}
                        onChange={(e) => {
                          const updatedTechnicians = [...(jobOrder.accomplished_by_technician || jobOrder.technicians || [])];
                          updatedTechnicians[i] = e.target.value;
                          handleInputChange('accomplished_by_technician', updatedTechnicians);
                        }}
                        disabled
                      />
                    </Grid>
                  ))}

                  {/* Accomplished Signature */}
                  <Grid item xs={12}>
                    {jobOrder.accomplishedSignature && (
                      <Box
                        component="img"
                        src={jobOrder.accomplishedSignature}
                        alt="Accomplished Signature"
                        sx={{
                          width: "100%",
                          maxWidth: 400,
                          height: 120,
                          objectFit: "contain",
                          border: "1px solid rgba(0,0,0,0.3)",
                          mb: 2,
                        }}
                      />
                    )}
                  </Grid>

                  {/* Technician Signatory dropdown, right below the signature */}
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="technician-signatory-label">
                        Technician Signatory
                      </InputLabel>
                      <Select
                        labelId="technician-signatory-label"
                        value={jobOrder.technician_signatory || ""}
                        label="Technician Signatory"
                        onChange={(e) => handleInputChange('technician_signatory', e.target.value)}
                        disabled
                      >
                        {jobOrder.technicians?.map((tech) => (
                          <MenuItem
                            key={tech.id}
                            value={`${tech.firstname} ${tech.lastname}`}
                          >
                            {`${tech.firstname} ${tech.lastname}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                  {!isTechnician && jobOrder?.technician_signature_uploaded_at && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                        <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                          🔷 Technician Signature Uploaded At:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                          {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Schedule Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Schedule
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Start Date"
                      name="job_date"
                      type="date"
                      value={jobOrder?.job_date || ""}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrder?.start_time || ""}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => handleInputChange('start_time', e.target.value)}
                    />
                  </Grid>
                  {/* <Grid item xs={12} md={6}>
                    <TextField
                      label="End Date"
                      name="end_date"
                      type="date"
                      value={jobOrder?.end_date || ""}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: {
                          min: jobOrder?.job_date || undefined
                        }
                      }}
                      onChange={(e) => handleInputChange('end_date', e.target.value)}
                    />
                  </Grid> */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrder?.end_time || ""}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => handleInputChange('end_time', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        
          {/* Acknowledged Signature Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="subtitle2"
                    sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    For Client Only
                  </Typography>
                }
                sx={{ px: 2, pt: 2, pb: 0 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Your Satisfaction Rating:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  (1 – lowest, 5 – highest)
                </Typography>
                <Rating
                  name="satisfaction-rating"
                  value={Number(jobOrder.satisfaction_rating) || 0}
                  onChange={(event, newValue) => handleInputChange('satisfaction_rating', newValue)}
                  readOnly
                />

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                  Acknowledged By:
                </Typography>
                {jobOrder.acknowledgedSignature && (
                  <Box
                    component="img"
                    src={jobOrder.acknowledgedSignature}
                    alt="Acknowledged Signature"
                    sx={{
                      width: "100%",
                      maxWidth: 400,
                      height: 120,
                      objectFit: "contain",
                      border: "1px solid rgba(0,0,0,0.3)",
                      mb: 2,
                    }}
                  />
                )}
                <TextField
                  label="Client Representative"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={jobOrder.acknowledgedClientSignatory || ""}
                  onChange={(e) => handleInputChange('acknowledgedClientSignatory', e.target.value)}
                  disabled
                />

                {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder?.client_signature_uploaded_at && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                      🟢 Client Signature Uploaded At:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                      {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      })}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Document Links Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {jobOrder?.receipts_url ? (
                    <>
                      {jobOrder?.receipts_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={jobOrder.receipts_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Receipts.pdf{" "}
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary">
                        No document links available.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
          {!isTechnician && jobOrder?.report_submitted_at && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                  📋 Report Submitted At:
                </Typography>
                <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                  {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  })}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Error Message Display */}
          {errorMessage && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            </Grid>
          )}

          {/* Save and Cancel Button Section */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
            mt={2}
            mb={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (returnTo) {
                  window.location.href = returnTo;
                } else {
                  navigate("/manage-service-reports");
                }
              }}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
                minWidth: 100
              }}
              disabled={isLoading || isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isLoading || isSaving}
              sx={{ minWidth: 100 }}
            >
              {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Grid>
        </Grid>
      </BaseFormWrapper>
    </FormProvider>
  );
}

export default EditTermiteControlForm;
